import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostBinding,
  Inject,
  Input,
  OnDestroy,
  OnInit
} from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { ICategory, IQuestion, SdFaqComponent } from '@seb/web-design-system/faqs';
import { ISdFormComponentItem, PositionEnum } from '@seb/web-design-system/core';
import { FaqEntry, FaqJsonLd, WC_CONFIG_PROVIDER, WcConfig } from '../../../models';
import { PdpTypeEnum } from '../../../enums';
import { FAQMapper } from '../../../mappers';
import { BaseStoreService, FaqService, TrackingService } from '../../../services';
import { CustomJsonLdDirective } from '../../../directives';

@Component({
  selector: 'wc-base-faq',
  templateUrl: './base-faq.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [SdFaqComponent, CustomJsonLdDirective]
})
export class BaseFaqComponent implements OnInit, OnDestroy {
  isContactButton = false;
  url: string;
  categories: ICategory[];
  answerReasons: ISdFormComponentItem[];
  pdpTypeEnum = PdpTypeEnum;
  iconPos: PositionEnum.left | PositionEnum.right = PositionEnum.left;
  jsonLdFaqs?: FaqJsonLd;

  @Input() addjsonLdFaqs = true;

  @Input() pdpType: PdpTypeEnum;

  @Input() class = '';

  protected readonly destroy$: Subject<void> = new Subject();

  @Input() set faqEntries(faqs: FaqEntry[]) {
    this.categories = this.faqMapper.toCategories(faqs);
    if (this.addjsonLdFaqs) {
      this.jsonLdFaqs = this.faqMapper.toJsonLdFaqs(faqs);
    }
  }

  @Input() titleLevel = 3;

  @HostBinding('class')
  get hostClasses(): string {
    return [this.class, 'wc-component is-base'].filter(Boolean).join(' ');
  }
  constructor(
    private faqMapper: FAQMapper,
    private faqService: FaqService,
    private baseStoreService: BaseStoreService,
    private trackingService: TrackingService,
    private cdRef: ChangeDetectorRef,
    @Inject(WC_CONFIG_PROVIDER) protected wcConfig: WcConfig
  ) {}

  ngOnInit(): void {
    const baseStore = this.baseStoreService.baseStore;
    this.isContactButton = baseStore.enableContanctUsInFAQ;
    this.url = this.isContactButton ? new URL(baseStore.linkContanctUs).pathname : '';
    this.cdRef.markForCheck();

    this.faqService
      .getFaqsNotUsefulReasons()
      .pipe(takeUntil(this.destroy$))
      .subscribe((notUsefulReasons) => {
        this.answerReasons = this.faqMapper.toAnswerReasons(notUsefulReasons);
        this.cdRef.markForCheck();
      });
    this.iconPos = this.wcConfig?.iconPos || PositionEnum.left;
  }

  onUsefulClick(usefulResponse: { isUseful: boolean; reason?: string; questionId: string }): void {
    this.faqService.voteForFaq(usefulResponse.questionId, usefulResponse.isUseful, usefulResponse.reason).subscribe();
    let questionSelected: IQuestion;
    const categorySelected = this.categories?.find((category) => {
      questionSelected = category.questions?.find((question) => {
        return question.id === usefulResponse.questionId;
      });
      return !!questionSelected;
    });
    this.trackingService.sendEvent('FAQFeedback', {
      faqTopic: categorySelected?.title,
      faqQuestion: questionSelected?.question,
      faqFeedback: usefulResponse.isUseful
        ? 'yes'
        : this.answerReasons?.find((answer) => answer.value === usefulResponse.reason)?.libelle
    });
  }

  onCategoryOpened(categoryName: string): void {
    this.trackingService.sendEvent('FAQ', { faqTopic: categoryName });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
